/**
 * Shared storefront payment-client helpers (feature 0026 C2 S5) — the framework-free client calls the
 * buy-box makes through the storefront's same-origin proxies. Extracted here so BOTH the standalone
 * `cod-buybox` section AND the block-based `main-product` buy-box run the SAME proven flow (one source of
 * truth): learn whether the store accepts cards (`/api/payments/status`), then, after a CARD order is
 * placed, create its PaymentIntent (`/api/payments/intent`). The proxies forward the store identity +
 * visitor IP server-side; the browser sends only the order id (+ the dev `?store` shortcut). The returned
 * `clientSecret` is a bearer credential — held in memory, passed to Stripe, NEVER logged.
 *
 * SSR-safe: every function guards `window`/`fetch`, so importing this module has no server-side effect.
 */
/**
 * One idempotency key per buy-box mount; regenerated after a confirmed success so a second intentional
 * order is allowed while a double-submit within one mount dedupes on the backend.
 */
export function makeIdempotencyKey() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        return crypto.randomUUID();
    return `cod-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
/** The dev-only `?store=<slug>` shortcut, read from the current URL (client-only). Absent in prod. */
export function devStoreParam() {
    if (typeof window === 'undefined')
        return null;
    try {
        return new URLSearchParams(window.location.search).get('store');
    }
    catch {
        return null;
    }
}
/**
 * Absolute `/checkout/return` URL Stripe redirects to after the buyer confirms the card (client-only).
 *
 * [orderId] (feature 0069 lot D) rides along so the return page can fetch that order and render the real
 * confirmation. It is the order's random-UUID handle — the same one the Cash-on-Delivery thank-you page
 * already carries in its URL — so it names nothing a stranger could guess and reveals no store data.
 * Omitted, the page behaves exactly as it did before this lot.
 */
export function buildReturnUrl(orderId) {
    const store = devStoreParam();
    const params = new URLSearchParams();
    if (store)
        params.set('store', store);
    if (orderId)
        params.set('order', orderId);
    const qs = params.toString();
    const path = qs ? `/checkout/return?${qs}` : '/checkout/return';
    if (typeof window !== 'undefined') {
        try {
            return new URL(path, window.location.origin).toString();
        }
        catch {
            /* fall through */
        }
    }
    return path;
}
/**
 * Ask the backend (via the same-origin proxy) whether this store accepts cards. Fail-safe: any
 * network/proxy/parse error resolves to `false` (COD-only). Callers gate this on a LIVE storefront (not the
 * builder / offline demo) so authoring/previews stay COD-only + offline.
 */
export async function fetchCardEnabled() {
    if (typeof fetch === 'undefined')
        return false;
    const store = devStoreParam();
    const url = store
        ? `/api/payments/status?store=${encodeURIComponent(store)}`
        : '/api/payments/status';
    try {
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok)
            return false;
        const data = (await res.json());
        return data?.cardEnabled === true;
    }
    catch {
        return false;
    }
}
/**
 * Create (idempotently) the PaymentIntent for a placed CARD order via the same-origin proxy. The proxy
 * forwards the store identity + visitor IP server-side; we send only the order id (+ the dev store shortcut).
 * The `clientSecret` it returns is a bearer credential — held in memory, passed to the card slot, NEVER logged.
 */
export async function createPaymentIntent(orderId, store) {
    let res;
    try {
        res = await fetch('/api/payments/intent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // Feature 0069 lot E — the buy-box says the same sentence under its card field. The SERVER still
            // decides: it AND-s this with the offers app being active, so a card is never kept by a shop with
            // nothing to offer.
            body: JSON.stringify({ orderId, store, keepCardForOffer: true }),
        });
    }
    catch {
        return { kind: 'error' };
    }
    const data = (await res.json().catch(() => null));
    if (res.ok && data?.kind === 'ok' && data.clientSecret && data.stripeAccountId) {
        return { kind: 'ok', clientSecret: data.clientSecret, stripeAccountId: data.stripeAccountId };
    }
    if (data?.kind === 'not-found' || data?.kind === 'not-payable' || data?.kind === 'rate-limited') {
        return { kind: data.kind };
    }
    return { kind: 'error' };
}
//# sourceMappingURL=paymentClient.js.map